<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row>
        <v-layout row justify-start align-end>
          <v-flex shrink>
            <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 5px 0px; height: 30px; width: 30px"
                  flat
                  fab
                  v-on="on"
                  color="primary"
                  @click="openViewDialog('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
              <template v-slot:activator="{ on }">
                <v-btn
                  style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
                  fab
                  flat
                  v-on="on"
                  color="primary"
                  @click="openViewDialog('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row justify-end>
          <v-flex xs5 pa-2>
            <v-autocomplete
              v-if="hasPermissions(['outlet_select'])"
              v-model="outlet_id"
              :label="`${$t('Outlet')} *`"
              hide-details
              :items="outletsList"
              @input="updateDataResult()"
              data-vv-name="outlet"
              :error="errors.has('outlet')"
              v-validate="'required'"
            />
          </v-flex>

          <v-flex xs3 pa-2>
            <v-menu v-model="dateMenu" full-width max-width="290" :close-on-content-click="false">
              <v-text-field
                slot="activator"
                :value="date"
                :label="$t('Date')"
                prepend-inner-icon="event"
                hide-details
                readonly
                name="Date"
                :error="errors.has('Date')"
                v-validate="'required|date_format:yyyy-MM-dd'"
              />
              <v-date-picker @input="updateDataResult()" @change="dateMenu = false" v-model="date" :max="today" />
            </v-menu>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4 pt-4">
      <v-progress-linear height="3" v-if="loading" :indeterminate="true" />
      <v-layout v-if="showData" row wrap class="income-statement-border">
        <v-flex xs12 pa-2 class="statement-titles">{{ $t('Revenue') }}</v-flex>
        <v-flex xs12>
          <v-layout pa-2 row wrap v-for="(r, index) in responseData.revenue" :key="index" class="bottom-border">
            <v-flex xs6>{{ $t(r.account_chart_name) }}</v-flex>
            <v-flex xs6>{{ r.balance | currency }}</v-flex>
          </v-layout>
          <v-layout class="bottom-border" pa-2 row wrap style="font-weight: bold">
            <v-flex xs6>{{ $t('Total Revenue') }}</v-flex>
            <v-flex xs6>{{ responseData.total_revenue | currency }}</v-flex>
          </v-layout>
        </v-flex>

        <v-flex xs12 pa-2 class="statement-titles">{{ $t('Expenses') }}</v-flex>
        <v-flex xs12>
          <v-layout pa-2 row wrap v-for="(e, index) in responseData.expenses" :key="index" class="bottom-border">
            <v-flex xs6>{{ $t(e.account_chart_name) }}</v-flex>
            <v-flex xs6>{{ e.balance | currency }}</v-flex>
          </v-layout>
          <v-layout pa-2 row wrap style="font-weight: bold">
            <v-flex xs6>{{ $t('Total Expenses') }}</v-flex>
            <v-flex xs6>{{ responseData.total_expenses | currency }}</v-flex>
          </v-layout>
        </v-flex>

        <v-flex pa-2 class="top-border" xs6>{{
          responseData.net_income > 0 ? $t('Net Income') : $t('Net Loss')
        }}</v-flex>
        <v-flex class="top-border" xs6 pt-2 pb-2>{{ responseData.net_income | currency }}</v-flex>

        <template v-if="settings.revenue_tax === 'Active'">
          <v-flex xs6 class="bottom-border" style="font-weight: bold" pa-2 v-if="!responseData.paid_income_tax">
            {{ $t('Due Income Tax Payment') }}
          </v-flex>
          <v-flex xs6 class="bottom-border" style="font-weight: bold" pt-2 pb-2 v-if="!responseData.paid_income_tax">
            {{ responseData.income_tax | currency }} ({{ responseData.tax_percentage | currency }}%)
          </v-flex>
          <v-flex xs6 style="font-weight: bold" pa-2>{{ $t('Paid Income Tax') }}</v-flex>
          <v-flex xs6 style="font-weight: bold" pt-2 pb-2>
            {{ responseData.paid_income_tax | currency }} ({{ responseData.paid_tax_percentage | currency }}%)
          </v-flex>
          <v-flex xs6 class="top-border" pa-2>{{
            responseData.net_income_after_tax > 0 ? $t('Net Income After Tax') : $t('Net Loss After Tax')
          }}</v-flex>
          <v-flex xs6 class="top-border" pt-2 pb-2>{{ responseData.net_income_after_tax | currency }}</v-flex>
        </template>
      </v-layout>
    </v-card>

    <view-income-statement
      :viewDialog="dialogs.viewDialog"
      @hidemodal="dialogs.viewDialog = false"
      :responseData="responseData"
      :clickedButton="clickedButton"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    viewIncomeStatement: () => import('./viewIncomeStatement.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false
      },
      clickedButton: '',
      showData: false,
      dateMenu: false,
      loading: false,
      today: this.$moment()
        .format()
        .substr(0, 10),
      date: this.$moment()
        .format()
        .substr(0, 10),
      outlet_id: 0,
      responseData: {}
    }
  },

  computed: {
    ...mapGetters({
      outletsList: 'outlets/getFullReportOutletsList',
      settings: 'configurations/getSystemSettings'
    })
  },

  mounted() {
    this.showData = false

    this.fetchOutletList()
    this.updateDataResult()

    this.setAppBarTitle({
      title: this.$t('Income Statement'),
      icon: 'fa-chart-line'
    })
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      fetchOutletList: 'outlets/setFullOutletsList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    openViewDialog(clickedButton) {
      this.clickedButton = clickedButton
      this.dialogs.viewDialog = true
    },

    updateDataResult() {
      this.loading = true
      Axios.get('income_statement', {
        params: {
          date: this.date,
          outlet_id: this.outlet_id
        }
      }).then(response => {
        this.responseData = {}
        this.responseData = response.data.data
        this.showData = true
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
.income-statement-border {
  border: 1px solid gray;
}
.statement-titles {
  border-bottom: 1px solid gray;
  background-color: lightgray;
  font-size: 16px;
  font-weight: bold;
}
.bottom-border {
  border-bottom: 1px solid gray;
}
.top-border {
  background-color: lightgray;
  border-top: 1px solid gray;
  font-weight: bold;
}
</style>
